import React from "react";
import {
    Button,
    ButtonType,
    Icon,
    IconSize,
    IStringInputModel,
    Modal,
    PageContainer,
    PageHeader
} from "@reapptor-apps/reapptor-react-components";
import AnonymousPage from "@/models/base/AnonymousPage";
import {
    AppointmentBookingServicePointFilter,
    AppointmentBookingServicePointFilterOnChange
} from "@/pages/AppointmentBooking/AppointmentBookingServicePointFilter/AppointmentBookingServicePointFilter";
import {
    AppointmentBookingServicePointList,
    AppointmentBookingServicePointListPagination,
    PossibleAppointment
} from "@/pages/AppointmentBooking/AppointmentBookingServicePointList/AppointmentBookingServicePointList";
import styles from "./AppointmentBooking.module.scss";
import GetAppointmentsInternalRequest from "@/models/server/requests/GetAppointmentsInternalRequest";
import {AppointmentFilterType} from "@/models/Enums";
import FenixAppController from "@/pages/FenixAppController";
import ReserveTimeModal from "@/components/ReserveTimeModal/ReserveTimeModal";
import ReservationGuideModal from "@/components/ReservationGuideModal/ReservationGuideModal";
import {IDoctor} from "@/models/interfaces/IDoctor";
import {IMedicalField} from "@/models/interfaces/IMedicalField";
import {INameExternalId} from "@/models/interfaces/INameExternalId";
import CancelAppointmentModal from "@/components/CancelAppointmentModal/CancelAppointmentModal";
import {ApiProvider, BasePageParameters} from "@reapptor-apps/reapptor-react-common";
import {ILocation} from "@/models/interfaces/ILocation";
import Localizer from "@/localization/Localizer";
import moment from "moment-timezone";
import {DateTime} from "luxon";
import {DynamicPageData, DynamicPageResponse} from "@/models/cms/DynamicPageResponse";
import {LocationsData} from "@/components/Footer/Footer";
import MetaTags from "@/components/MetaTags/MetaTags";
import ReactMarkdown from "react-markdown";
import {Kela65InfoBox} from "@/models/cms/Kela65InfoBox"
import type {SingleTypeResponse} from "@/models/cms/SingleTypeResponse";

export interface IAppointmentBookingParams extends BasePageParameters {
    selectedLocation?: string | undefined;
    selectedDoctor?: string | undefined;
    selectedCare?: string | undefined;
    selectedDate?: string | null;
    occupationalHealth?: string | undefined;
    kela65?: string | undefined;
}

interface IAppointmentBookingProps {
}

interface IAppointmentBookingState {
    locationsData: DynamicPageData[];
    availableServices: IMedicalField[];

    availableDoctors: IDoctor[];

    availableLocations: INameExternalId[];

    filterValue: AppointmentBookingServicePointFilterOnChange | null;
    /**
     * Implemented test pagination system
     * TODO: Replace when pagination implemented
     */
    pagedPossibleAppointmentData: AppointmentBookingServicePointListPagination<PossibleAppointment>;

    filterData: IFilterData | null;

    firstFilterIndex: AppointmentFilterType;

    secondFilterIndex: AppointmentFilterType;

    defaultLocation: INameExternalId | null;

    defaultSpecialist: IDoctor | null;

    defaultMedicalField: IMedicalField | null;

    defaultDate: Date | null;

    paramsProcessed: boolean;

    timeBookingForOccupationalHealth: boolean;
    timeBookingForKela65: boolean;
    showOccupationalHealthInfoBox: boolean;
    
    kela65InfoBox: Kela65InfoBox | null;
    showKela65InfoBox: boolean;
}

interface IFilterData {
    medicalFields: IMedicalField[];

    doctors: IDoctor[];

    locations: INameExternalId[];
}

export default class AppointmentBooking extends AnonymousPage<IAppointmentBookingProps, IAppointmentBookingState> {
    public firstname: IStringInputModel = {value: ""};
    public lastname: IStringInputModel = {value: ""};
    public ssn: IStringInputModel = {value: ""};

    public _loginModal: React.RefObject<Modal> = React.createRef();
    public _appointmentModal: React.RefObject<Modal> = React.createRef();

    private readonly _reserveTimeModal: React.RefObject<ReserveTimeModal> = React.createRef();
    private readonly _cancelAppointmentModal: React.RefObject<CancelAppointmentModal> = React.createRef();
    private readonly _reservationGuideModal: React.RefObject<ReservationGuideModal> = React.createRef();
    public formRef: React.RefObject<any> = React.createRef();

    state: IAppointmentBookingState = {
        locationsData: [],
        availableServices: [],
        availableLocations: [],
        availableDoctors: [],
        filterValue: null,
        filterData: null,
        firstFilterIndex: AppointmentFilterType.None,
        secondFilterIndex: AppointmentFilterType.None,
        defaultLocation: null,
        defaultDate: null,
        paramsProcessed: false,
        timeBookingForOccupationalHealth: false,
        timeBookingForKela65: false,
        defaultSpecialist: null,
        defaultMedicalField: null,
        showOccupationalHealthInfoBox: true,
        kela65InfoBox: null,
        showKela65InfoBox: false,
        pagedPossibleAppointmentData: {
            loading: false,
            pageIndex: 0,
            pageSize: 5,
            totalCount: 25,
            items: []
        }
    };

    public getTitle(): string {
        return Localizer.appointmentPageTitle;
    }

    private async initializeFiltersDataAsync(): Promise<void> {
        const params = this.parameters as IAppointmentBookingParams as IAppointmentBookingParams | null;
        
        if (!this.state.paramsProcessed) {
            if (params?.occupationalHealth === 'true') {
                await this.setState({timeBookingForOccupationalHealth: true});
            }
            
            if (params?.kela65 === 'true') {
                await this.setState({timeBookingForKela65: true});
            }
        }

        const dynamicData: IFilterData = await this.postAsync(
            "/api/Application/GetFilterData",
            {
                OccupationalHealth: this.state.timeBookingForOccupationalHealth,
                Kela65: this.state.timeBookingForKela65,
            }
        );

        if (params) {
            let dateValue = params.selectedDate ? moment(params.selectedDate).toDate() : null;
            const matching: INameExternalId | null = dynamicData.locations.filter((item) => item.id === params.selectedLocation)[0];
            const doctor: IDoctor | null = dynamicData.doctors.filter((item) => item.id === params.selectedDoctor)[0];
            const care: IMedicalField | null = dynamicData.medicalFields.filter((item) => item.id === params.selectedCare)[0];


            await this.setState({
                defaultLocation: matching,
                defaultDate: dateValue,
                defaultSpecialist: doctor,
                defaultMedicalField: care
            });
        } else {
            // hidden until Kela65 appointments are filtered correctly
            // await this._reservationGuideModal.current?.openAsync();
        }

        await this.setState({filterData: dynamicData});

        await this.setState({paramsProcessed: true});

        await this.setDefaultFiltersAsync();
    }

    public async initializeAsync(): Promise<void> {
        await super.initializeAsync();
        window.scrollTo(0, 0);

        const locationsData: DynamicPageResponse = await this.getAsync("/api/Application/GetDynamicPageContent?contentType=locations&deepPopulate=openingHours&sort=externalId");
        if (locationsData && locationsData?.data) {
            await this.setState({locationsData: locationsData.data})
        }

        const kela65InfoBox: SingleTypeResponse<Kela65InfoBox> = await this.getAsync("/api/Application/GetDynamicPageContent?contentType=kela65-info-box&populate=*");
        if (kela65InfoBox && kela65InfoBox?.data?.attributes?.enabled) {
            await this.setState({ kela65InfoBox: kela65InfoBox.data.attributes, showKela65InfoBox: true})
        }

        await this.initializeFiltersDataAsync();
    }

    async closeInfoBox(): Promise<void> {
        await this.setState({showOccupationalHealthInfoBox: false});
    }

    async closeKelaInfoBox(): Promise<void> {
        await this.setState({showKela65InfoBox: false});
    }

    public async setDefaultFiltersAsync(): Promise<void> {
        if (this.state.filterData) {
            await this.setState({
                availableServices: this.state.filterData?.medicalFields ?? [],
                availableDoctors: this.state.filterData?.doctors ?? [],
                availableLocations: this.state.filterData?.locations ?? []
            });
        }
    }

    private async fetchData(filterValue: AppointmentBookingServicePointFilterOnChange | null) {
        const specialist: IDoctor = filterValue?.specialist as unknown as IDoctor;
        const location: ILocation | null = filterValue?.location as any as ILocation;
        const medicalField: IMedicalField = filterValue?.medicalField as unknown as IMedicalField;

        //DST magic

        const timeRangeStart = DateTime.fromJSDate(filterValue!.timeRangeStart).setZone('Europe/Helsinki')
        let offset = timeRangeStart.isInDST ? 180 : 120;
        let startDateOffset;
        let endDateOffset;

        //const nowIsDST = DateTime.fromJSDate(new Date()).isInDST;
        const startDateIsDST = DateTime.fromJSDate(filterValue!.dateRangeStart).isInDST
        const endDateIsDST = DateTime.fromJSDate(filterValue!.dateRangeEnd).isInDST

        /*//Check for edge cases when nearing DST change date. If searching while DST active and startDate is not DST => use non-DST offset, and vice versa.
        if(nowIsDST && !startDateIsDST){
            offset = 180;
        }

        if(!nowIsDST && startDateIsDST){
            offset = 120;
        }*/

        startDateIsDST ? startDateOffset = 180 : startDateOffset = 120;
        endDateIsDST ? endDateOffset = 180 : endDateOffset = 120;

        const request: GetAppointmentsInternalRequest = {
            dateRangeEnd: DateTime.fromJSDate(filterValue!.dateRangeEnd).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toUTC(endDateOffset,{keepLocalTime: true}).toJSDate(),
            timeRangeStart: DateTime.fromJSDate(filterValue!.timeRangeStart).toUTC(offset, {keepLocalTime: true}).toJSDate(),
            timeRangeEnd: DateTime.fromJSDate(filterValue!.timeRangeEnd).toUTC(offset, {keepLocalTime: true}).toJSDate(),
            dateRangeStart: DateTime.fromJSDate(filterValue!.dateRangeStart).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toUTC(startDateOffset,{keepLocalTime: true}).toJSDate(),
            selectedService: medicalField?.id ? [medicalField.id] : filterValue?.medicalField ? [filterValue.medicalField.id] : [],
            selectedPractitioners: specialist?.id ? [specialist.id] : filterValue?.specialist ? [filterValue.specialist.id] : [],
            selectedClinics: location?.id ? [location.id] : filterValue?.location ? [filterValue.location.id] : [],
            occupationalHealth: this.state.timeBookingForOccupationalHealth
        };

        const data: PossibleAppointment[] = await this.postAsync("/api/Application/GetAvailableAppointments", request);
        const pagedThing: AppointmentBookingServicePointListPagination<PossibleAppointment> = {
            loading: false,
            pageIndex: 0,
            items: data,
            pageSize: 5,
            totalCount: data.length
        };

        await this.setState({pagedPossibleAppointmentData: pagedThing});
    }

    private async reserveTimeForOccupationalHealthAsync(): Promise<void> {
        await FenixAppController.openOccupationalAppointments()
    }

    private async reserveTimeForKela65Async(): Promise<void> {
        await FenixAppController.openKela65Appointments()
    }

    private async reserveTimeForNormalAppointmentsAsync(): Promise<void> {
        await FenixAppController.openAppointmentsPage();
    }

    private openRemoteReception() {
        window.location.href = "https://www.laakarikeskusfenix.fi/DigiFenix";
        return Promise.resolve(undefined);
    }

    public render(): React.ReactNode {
        return (
            <PageContainer fullWidth className={styles.pageContainer}>
                <MetaTags pageName="AppointmentBooking" />
                <PageHeader title={Localizer.topNavAppointmentBooking} className={styles.pageHeader}></PageHeader>
                <div className={styles.pageContent}>
                    <div className={styles.fluidContent}>
                        {this.state.showOccupationalHealthInfoBox &&
                            <div className={styles.occupationalHealthInfoBox}>
                                <Icon name={"times"} size={IconSize.Large} style={2} customStyle={{position: "absolute", top: "0.75rem", right: "0.75rem", cursor: "pointer"}} onClick={() => this.closeInfoBox()}/>
                                <div className={styles.infoBoxTextDiv}>
                                    <div>
                                        <strong>{Localizer.appointmentPageOccupationalHealthInfo}</strong>
                                    </div>
                                </div>
                            </div>
                        }
                        {(this.state.kela65InfoBox && this.state.showKela65InfoBox) &&
                            <div className={styles.occupationalHealthInfoBox}>
                                <Icon name={"times"} size={IconSize.Large} style={2} customStyle={{position: "absolute", top: "0.75rem", right: "0.75rem", cursor: "pointer"}} onClick={() => this.closeKelaInfoBox()}/>
                                <div className={styles.infoBoxTextDiv}>
                                    <div>
                                        <p>
                                            <strong>{this.state.kela65InfoBox.title}</strong>
                                        </p>
                                        <ReactMarkdown>{this.state.kela65InfoBox.content}</ReactMarkdown>
                                    </div>
                                </div>
                            </div>
                        }
                        <div className={styles.reserveTimeCard}>
                            <div className={styles.cardHeader}>
                                <Icon name="clock" style={2} size={IconSize.Large}/>
                                <span>{Localizer.buttonBookAppointment}</span>
                            </div>
                            <div className={styles.reserveTimeNav}>
                                <Button
                                    label={Localizer.homePageHeaderLocation}
                                    type={!this.state.timeBookingForOccupationalHealth && !this.state.timeBookingForKela65 ? ButtonType.Primary : ButtonType.Default}
                                    onClick={async () => await this.reserveTimeForNormalAppointmentsAsync()}
                                    icon={"hospital"}
                                    className={styles.appointmentButton}
                                />
                                <Button
                                    label={Localizer.homePageHeaderOccupationalHealthBooking}
                                    icon={"hospital"}
                                    type={this.state.timeBookingForOccupationalHealth ? ButtonType.Primary : ButtonType.Default}
                                    onClick={async () => await this.reserveTimeForOccupationalHealthAsync()}
                                    className={styles.appointmentButton}
                                />
                              {/* // hidden until Kela65 appointments are filtered correctly
                                <Button
                                    label={Localizer.appointmentPageKela65Label}
                                    icon={"hospital"}
                                    type={this.state.timeBookingForKela65 ? ButtonType.Primary : ButtonType.Default}
                                    onClick={async () => await this.reserveTimeForKela65Async()}
                                    className={styles.appointmentButton}
                                />
                              */}
                                <Button
                                    label={Localizer.homePageHeaderRemoteBooking}
                                    type={ButtonType.Default}
                                    icon={"laptop-medical"}
                                    onClick={() => this.openRemoteReception()}
                                    className={styles.appointmentButton}
                                />
                                <Button
                                    label={Localizer.appointmentPageAppointmentCanceling}
                                    type={ButtonType.Default}
                                    icon={"ban"}
                                    onClick={async () => await this._cancelAppointmentModal.current?.openAsync()}
                                    className={styles.appointmentButton}
                                />
                            </div>

                            {this.state.paramsProcessed && (
                                <AppointmentBookingServicePointFilter
                                    defaultDate={this.state.defaultDate}
                                    defaultLocation={this.state.defaultLocation}
                                    defaultSpecialist={this.state.defaultSpecialist}
                                    defaultMedicalField={this.state.defaultMedicalField}
                                    medicalFieldList={this.state.availableServices}
                                    specialistList={this.state.availableDoctors}
                                    locationList={this.state.availableLocations}
                                    onChange={async (filterValue) => await this.fetchData(filterValue)}
                                />
                            )}

                            <div className={styles.search}>
                                <Button label={Localizer.genericSearch} type={ButtonType.Primary}/>
                            </div>
                            <AppointmentBookingServicePointList
                                pagedData={this.state.pagedPossibleAppointmentData}
                                onShowMoreClick={async () => {
                                    //  Test pagination setup
                                    this.setState({
                                        pagedPossibleAppointmentData: {
                                            ...this.state.pagedPossibleAppointmentData,
                                            loading: true
                                        }
                                    });
                                    //  Simulate data fetching.
                                    setTimeout(() => {
                                        this.setState({
                                            pagedPossibleAppointmentData: {
                                                ...this.state.pagedPossibleAppointmentData,
                                                pageIndex: this.state.pagedPossibleAppointmentData.pageIndex + 1,
                                                items: [...this.state.pagedPossibleAppointmentData.items],
                                                loading: false
                                            }
                                        });
                                    }, 1000);
                                }}
                                onReserveTimeClick={async (possibleAppointment: PossibleAppointment) => {
                                    this._reserveTimeModal.current?.openAsync(
                                        possibleAppointment,
                                        this.state.timeBookingForOccupationalHealth,
                                      this.state.locationsData
                                    );
                                }}
                            ></AppointmentBookingServicePointList>
                        </div>
                        <div className={styles.cancelAppointmentContainer}>
                            <h3>{Localizer.appointmentPageWantToCancelAppointment}</h3>
                            <Button
                              label={Localizer.appointmentPageAppointmentCanceling}
                              type={ButtonType.Primary}
                              className={styles.appointmentButton}
                              onClick={async () => await this._cancelAppointmentModal.current?.openAsync()}
                            />
                        </div>
                    </div>
                </div>

                <ReserveTimeModal
                    ref={this._reserveTimeModal}
                    onTimeReserved={(sender, time: PossibleAppointment) => FenixAppController.onReserveTime(time)}
                />

                <CancelAppointmentModal
                    ref={this._cancelAppointmentModal}
                    onTimeReserved={(sender, time: PossibleAppointment) => FenixAppController.onReserveTime(time)}
                />
                
                <ReservationGuideModal ref={this._reservationGuideModal} />
            </PageContainer>
        );
    }
}
